import { HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BrowserControlCommunicationService } from '@applications/sha-services';
import { createEffect } from '@ngrx/effects';
import { DataPersistence } from '@nrwl/angular';
import { ApiService } from '@quorum/api';
import { fromRouter } from '@quorum/sha-router';
import { CookieService } from 'ngx-cookie-service';
import { EMPTY, from, of } from 'rxjs';
import { concatMap, delay, map, mergeMap, take } from 'rxjs/operators';
import { AuthenticationStateService } from '../authentication-state.service';
import * as fromAuthentication from './authentication.actions';
import { AuthenticatedUser, AuthenticationState } from './authentication.interfaces';

@Injectable()
export class AuthenticatedUserEffects {
  private readonly RETURN_URL_KEY = 'postLoginReturnUrl';
  private readonly USER_VER_KEY = 'postLoginUserVersion';

  private getCookieDomain(): string {
    const domain = window.location.hostname || window.document.domain;

    if (this.browserCommunicationService.embeddedInXsellerator) {
      console.log('[Embedded Browser] domain:', domain);
    }

    if (domain.indexOf('xsellerator.dev') > -1) {
      return domain.indexOf('gm2') > -1 ? 'gm2.xsellerator.dev' : 'gm.xsellerator.dev';
    } else if (domain.indexOf('xsellerator') > -1) {
      return '.xsellerator.com';
    } else if (domain.indexOf('qcommunicator') > -1) {
      return '.qcommunicator.com';
    } else if (domain.includes('192.168')) {
      return '192.168';
    } else if (domain.indexOf('halcyontek') > -1) {
      return '.halcyontek.com';
    } else {
      return 'localhost';
    }
  }

  private clearAuthCookies(): void {
    const domain = this.getCookieDomain();
    this.cookieService.delete('quorum', '/', domain);
    this.cookieService.delete('X-Source', '/', domain);
    localStorage.clear();
  }

  private isLocalDev(): boolean {
    return window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  }

  private stripLeadingVersion(pathname: string): string {
    // "/alpha/vehicle/1" -> "/vehicle/1"
    // "/beta" -> "/"
    // "/" -> "/"
    const stripped = pathname.replace(/^\/(alpha|beta|gr)(\/|$)/, '/');
    return stripped.startsWith('/') ? stripped : '/' + stripped;
  }

  private joinVersionPath(versionBase: string, remainderPath: string): string {
    // versionBase: "/alpha/" (always ends with "/")
    // remainderPath: "/vehicle/1" or "/" (always starts with "/")
    const base = versionBase.endsWith('/') ? versionBase : versionBase + '/';
    const rem = remainderPath.startsWith('/') ? remainderPath.slice(1) : remainderPath;
    // "/alpha/" + "vehicle/1" -> "/alpha/vehicle/1"
    return base + rem;
  }

  private buildVersionedUrl(userVersion: string, rawPathname: string, rawSearch: string): string {
    const expectedBase = `/${userVersion}/`;
    const remainder = this.stripLeadingVersion(rawPathname); // keeps leading "/"
    const versionedPath = this.joinVersionPath(expectedBase, remainder); // no double slashes
    return `${versionedPath}${rawSearch || ''}`;
  }

  private storeReturnUrl(url: string): void {
    // Store only meaningful deep links (not just "/", "/alpha/", etc.)
    if (!url) return;

    const pathOnly = url.split('?')[0] || '';
    const isJustVersionRoot = /^\/(alpha|beta|gr)\/?$/.test(pathOnly);
    const isLogin = pathOnly === '/login' || pathOnly.endsWith('/login');

    if (isLogin) return;

    // If it's "/" or "/alpha/" etc, still ok to store, but it won't help much.
    // Your call: I'm keeping "/" out to reduce noise.
    if (pathOnly === '/' || isJustVersionRoot) return;

    // Routes with query params or auxiliary outlets typically rely on NgRx state
    // that won't survive a session expiry. Go to root so the user starts clean.
    if (url.includes('?') || url.includes('(')) return;

    sessionStorage.setItem(this.RETURN_URL_KEY, url);
  }

  private getAndClearReturnUrl(): string | null {
    const url = sessionStorage.getItem(this.RETURN_URL_KEY);
    if (url) {
      sessionStorage.removeItem(this.RETURN_URL_KEY);
    }
    return url;
  }

  private setPostLoginUserVersion(version: string): void {
    if (version) sessionStorage.setItem(this.USER_VER_KEY, version.toLowerCase());
  }

  private getPostLoginUserVersion(): string | null {
    const v = sessionStorage.getItem(this.USER_VER_KEY);
    return v ? v.toLowerCase() : null;
  }

  private checkVersionAndRedirect(user: any): boolean {
    const userVersion = (user?.version || 'alpha').toLowerCase();
    const currentPath = window.location.pathname;
    const currentSearch = window.location.search || '';

    if (this.isLocalDev()) {
      return false;
    }

    const expectedBase = `/${userVersion}/`;

    // Authenticated users on any login route (e.g. /login, /alpha/login) are stuck
    // because monitorAuthenticationStages is only dispatched from the login form.
    // Redirect to the version root before checking the version prefix.
    const strippedPath = this.stripLeadingVersion(currentPath);
    const isLoginPath = strippedPath === '/login' || strippedPath.startsWith('/login/');
    if (isLoginPath) {
      window.location.replace(expectedBase);
      return true;
    }

    // Already correct version base? Match both /alpha and /alpha/...
    if (currentPath === `/${userVersion}` || currentPath.startsWith(expectedBase)) return false;

    const targetUrl = this.buildVersionedUrl(userVersion, currentPath, currentSearch);
    window.location.replace(targetUrl);
    return true;
  }

  private parseUrlToPath(url: string): string[] {
    const path = url.split('?')[0];
    return path.split('/').filter((segment) => segment.length > 0);
  }

  private getQueryParams(url: string): any {
    const queryString = url.split('?')[1];
    if (!queryString) return {};

    const params: any = {};
    queryString.split('&').forEach((param) => {
      const [key, value] = param.split('=');
      params[decodeURIComponent(key)] = decodeURIComponent(value || '');
    });
    return params;
  }

  authenticateUser = createEffect(() =>
    this.d.pessimisticUpdate(fromAuthentication.AUTHENTICATE_USER, {
      run: (a: fromAuthentication.AuthenticateUser) => {
        let headers: HttpHeaders = new HttpHeaders();
        headers = headers.set('Content-Type', 'application/json');

        const body = {
          grant_type: 'password',
          username: a.payload.username,
          password: a.payload.password,
        };

        return this.apiService
          .postCognitoAuth<string>('auth/login', body, {
            headers: headers,
            observe: 'response',
            withCredentials: true,
          })
          .pipe(
            concatMap((token: any) => {
              let newHeaders: HttpHeaders = new HttpHeaders();
              newHeaders = newHeaders.set('Authorization', 'Bearer ' + token.access_token);

              return this.apiService.getCognitoAuth<string>('auth/user-from-token', { headers: newHeaders }).pipe(
                mergeMap((user: any) => {
                  const domain = this.getCookieDomain();
                  const userVersion = (user?.version || 'alpha').toLowerCase();

                  // Persist auth + version
                  this.cookieService.set('quorum', token.access_token, 14, '/', domain);

                  // Store version for post-login return rewrite
                  this.setPostLoginUserVersion(userVersion);

                  // Version redirect is deferred to monitorAuthenticationStages so
                  // auth stages can complete visibly before the hard page navigation.
                  return from([new fromAuthentication.UserAuthenticated({ token: token.access_token, user: user })]);
                })
              );
            })
          );
      },
      onError: (_a: fromAuthentication.AuthenticateUser, error) => {
        return new fromAuthentication.UserAuthenticatedError({
          message: 'Invalid username or password.',
          error: error.reason,
        });
      },
    })
  );

  checkCookieForAuthentication = createEffect(() =>
    this.d.fetch(fromAuthentication.CHECK_COOKIE_FOR_AUTHENTICATION, {
      run: () => {
        const currentPath = window.location.pathname;
        const strippedPath = this.stripLeadingVersion(currentPath);
        const isLoginPath = strippedPath === '/login' || strippedPath.startsWith('/login/');

        // Capture deep link BEFORE we bounce to /login (skip login paths)
        if (!isLoginPath) {
          this.storeReturnUrl(currentPath + (window.location.search || ''));
        }

        let token: any = this.cookieService.get('quorum');

        // Check for token in URL params (non-login paths only)
        if (!isLoginPath) {
          const urlParams = new URLSearchParams(window.location.search);
          token = urlParams.get('token') ? urlParams.get('token') : token;
        }

        if (token) {
          let headers: HttpHeaders = new HttpHeaders();
          headers = headers.set('Authorization', 'Bearer ' + token);

          return this.apiService.getCognitoAuth<string>('auth/user-from-token', { headers: headers }).pipe(
            mergeMap((user: any) => {
              const domain = this.getCookieDomain();

              this.cookieService.set('quorum', token, 14, '/', domain);
              this.setPostLoginUserVersion((user?.version || 'alpha').toLowerCase());

              if (this.checkVersionAndRedirect(user)) {
                return EMPTY;
              }

              return of(new fromAuthentication.UserAuthenticated({ token: token, user: user }));
            })
          );
        } else if (this.browserCommunicationService.embeddedInXsellerator) {
          const silentToken = this.browserCommunicationService.getSilentLoginToken();

          return this.apiService.postCognitoAuth<string>('auth/silent-login', { token: silentToken }).pipe(
            map((silentLoginResponse) => silentLoginResponse.access_token),
            mergeMap((tokenGeneratedFromSilent: any) => {
              let headers: HttpHeaders = new HttpHeaders();
              headers = headers.set('Authorization', 'Bearer ' + tokenGeneratedFromSilent);

              return this.apiService.getCognitoAuth<string>('auth/user-from-token', { headers }).pipe(
                mergeMap((user: any) => {
                  this.setPostLoginUserVersion((user?.version || 'alpha').toLowerCase());

                  if (this.checkVersionAndRedirect(user)) {
                    return EMPTY;
                  }
                  return of(
                    new fromAuthentication.UserAuthenticated({ token: tokenGeneratedFromSilent, user: user })
                  );
                })
              );
            })
          );
        } else {
          this.clearAuthCookies();
          return new fromAuthentication.UserAuthenticatedError(null);
        }
      },
      onError: (_a: fromAuthentication.CheckCookieForAuthentication, error: any) => {
        if (this.browserCommunicationService.embeddedInXsellerator) {
          return new fromAuthentication.AttemptSilentLogin({});
        } else {
          // Stale/expired cookie at boot is a normal state — fail silently and let the user log in fresh.
          console.warn('checkCookieForAuthentication failed:', error?.status, error?.statusText);
          return new fromAuthentication.UserAuthenticatedError(null);
        }
      },
    })
  );

  attemptSilentLogin = createEffect(() =>
    this.d.pessimisticUpdate(fromAuthentication.ATTEMPT_SILENT_LOGIN, {
      run: () => {
        const silentToken = this.browserCommunicationService.getSilentLoginToken();

        return this.apiService.postCognitoAuth<string>('auth/silent-login', { token: silentToken }).pipe(
          map((silentLoginResponse) => silentLoginResponse.access_token),
          mergeMap((tokenGeneratedFromSilent: any) => {
            let headers: HttpHeaders = new HttpHeaders();
            headers = headers.set('Authorization', 'Bearer ' + tokenGeneratedFromSilent);

            return this.apiService.getCognitoAuth<string>('auth/user-from-token', { headers }).pipe(
              mergeMap((user: any) => {
                this.setPostLoginUserVersion((user?.version || 'alpha').toLowerCase());

                if (this.checkVersionAndRedirect(user)) {
                  return EMPTY;
                }

                return of(new fromAuthentication.UserAuthenticated({ token: tokenGeneratedFromSilent, user: user }));
              })
            );
          })
        );
      },
      onError: (_a: fromAuthentication.AttemptSilentLogin, error) => {
        return new fromAuthentication.UserAuthenticatedError({
          message: 'Error authenticating user.',
          error: error.reason,
        });
      },
    })
  );

  logout = createEffect(() =>
    this.d.pessimisticUpdate(fromAuthentication.LOGOUT, {
      run: (a: fromAuthentication.Logout, state: AuthenticationState) => {
        return this.apiService.postCognitoAuth<AuthenticatedUser>('auth/logout', state.authenticatedUser).pipe(
          mergeMap(() => {
            this.clearAuthCookies();
            sessionStorage.removeItem(this.RETURN_URL_KEY);
            sessionStorage.removeItem(this.USER_VER_KEY);
            return from([new fromAuthentication.LogoutSuccess({}), new fromRouter.Go({ path: ['login'] })]);
          })
        );
      },
      onError: (_a: fromAuthentication.Logout, error) => {
        this.clearAuthCookies();
        sessionStorage.removeItem(this.RETURN_URL_KEY);
        sessionStorage.removeItem(this.USER_VER_KEY);
        return new fromAuthentication.LogoutError(error);
      },
    })
  );

  getApiCredentials = createEffect(() =>
    this.d.pessimisticUpdate(fromAuthentication.GET_API_CREDENTIALS_FOR_EMPLOYEE, {
      run: (a: fromAuthentication.GetApiCredentialsForEmployee) => {
        this.apiService.post('oauth/test', { token: a.payload.token }).subscribe((key) => {
          if (!key.error) {
            this.authenticationStateService.quorumEmployeeLoginSuccessful(
              key.apiKey,
              key.destinationId, //ZZZ509 for staging/testing
              a.payload.redirectUrl
            );
          } else {
            this.authenticationStateService.quorumEmployeeLoginFailure();
          }
        });
      },
      onError: (_a: fromAuthentication.GetApiCredentialsForEmployee, error) => {
        console.error('Error getting API credentials:', error);
      },
    })
  );

  userAuthenticated = createEffect(() =>
    this.d.fetch(fromAuthentication.USER_AUTHENTICATED, {
      run: () => {
        // TODO: Re-enable data layer initialization when ready
      },
      onError: (a: fromAuthentication.UserAuthenticated, error) => {
        return new fromAuthentication.UserAuthenticatedError(error);
      },
    })
  );

  userAuthenticatedError = createEffect(() =>
    this.d.fetch(fromAuthentication.USER_AUTHENTICATED_ERROR, {
      run: () => {
        this.clearAuthCookies();
      },
      onError: (_a: fromAuthentication.UserAuthenticatedError, error) => {
        console.error('Error during user authenticated error effect:', error);
      },
    })
  );

  monitorAuthenticationStages = createEffect(() =>
    this.d.fetch(fromAuthentication.MONITOR_AUTHENTICATION_STAGES, {
      run: () => {
        return this.authenticationStateService.authenticationStagesComplete().pipe(
          take(1),
          delay(500),
          mergeMap(() => {
            const returnUrl = this.getAndClearReturnUrl();
            const userVersion = this.getPostLoginUserVersion() || 'alpha';

            // If the user's version doesn't match the current URL prefix (e.g., logged
            // in on /alpha as a beta user), do a hard redirect now that stages are done.
            if (!this.isLocalDev()) {
              const expectedBase = `/${userVersion}/`;
              const currentPath = window.location.pathname;
              const onCorrectVersion =
                currentPath === `/${userVersion}` || currentPath.startsWith(expectedBase);

              if (!onCorrectVersion) {
                let targetUrl = expectedBase;
                if (returnUrl) {
                  const rawPath = returnUrl.split('?')[0] || '/';
                  const rawSearch = returnUrl.includes('?') ? '?' + (returnUrl.split('?')[1] || '') : '';
                  targetUrl = this.buildVersionedUrl(userVersion, rawPath, rawSearch);
                }
                window.location.replace(targetUrl);
                return EMPTY;
              }
            }

            if (returnUrl) {
              // Rewrite returnUrl into the correct version path (critical for deep links)
              const rawPath = returnUrl.split('?')[0] || '/';
              const rawSearch = returnUrl.includes('?') ? '?' + (returnUrl.split('?')[1] || '') : '';
              const versionedReturn = this.buildVersionedUrl(userVersion, rawPath, rawSearch);

              // Embedded browsers: window.location is usually more reliable
              if (this.browserCommunicationService.embeddedInXsellerator) {
                window.location.href = versionedReturn;
                return EMPTY;
              }

              const path = this.parseUrlToPath(this.stripLeadingVersion(rawPath));
              const queryParams = this.getQueryParams(versionedReturn);

              return of(new fromRouter.Go({
                path,
                query: Object.keys(queryParams).length > 0 ? queryParams : undefined,
              }));
            }

            return of(new fromRouter.GoToRoot());
          })
        );
      },
      onError: (_a: fromAuthentication.MonitorAuthenticationStages, error) => {
        return new fromAuthentication.MonitorAuthenticationStagesError(error);
      },
    })
  );

  constructor(
    private apiService: ApiService,
    private cookieService: CookieService,
    private d: DataPersistence<AuthenticationState>,
    private authenticationStateService: AuthenticationStateService,
    private browserCommunicationService: BrowserControlCommunicationService
  ) {}
}
